import React, {useState, useMemo, useEffect, useRef} from 'react';

import {useMutation, useQuery} from '@apollo/client/react';
import {
  X,
  Users,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  Upload,
  FileText,
  Settings,
  Eye,
  EyeOff,
  Edit2,
  Trash2,
  Check,
  RefreshCw,
  Shield,
} from 'lucide-react';
import {
  parseEmailList,
  autoDetectSeparator,
  findDangerousDomains,
  findDuplicateEmails,
  getEmailListStats,
  type ParsedEmail,
} from '../../utils/emailValidation';
import {
  validateEmailsBulkWithMailgun,
  getMailgunResultDescription,
  getMailgunResultColor,
  type MailgunBulkValidationResult,
} from '../../services/mailgunValidation';
import {gql} from 'types/__generated__';
import {ORGANISATION_ROLES_QUERY, grantableRoles} from '../../graphql/roles';

// createUsersFromEmailString() derives the target organisation from the session token,
// which an admin login does not have ("No organisation selected yet"). createUser() takes
// the organisation explicitly, so it works from the admin app and imports into the
// organisation actually being viewed.
const CREATE_USER = gql(`
  mutation CreateUser($input: UserInput!) {
    createUser(input: $input)
  }
`);

interface BulkUserImportModalProps {
  organisationId: string;
  organisationName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type WizardStep = 'input' | 'review' | 'result';

export default function BulkUserImportModal({organisationId, organisationName, isOpen, onClose, onSuccess}: BulkUserImportModalProps) {
  const [step, setStep] = useState<WizardStep>('input');
  const [emailText, setEmailText] = useState('');
  const [autoDetect, setAutoDetect] = useState(true);
  const [detectedSeparator, setDetectedSeparator] = useState('\n');
  const [customSeparators, setCustomSeparators] = useState<string[]>(['\n']);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [ignoreWarnings, setIgnoreWarnings] = useState(false);
  const [editableEmails, setEditableEmails] = useState<ParsedEmail[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editEmail, setEditEmail] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editRoleId, setEditRoleId] = useState('');
  const [mailgunApiKey, setMailgunApiKey] = useState('');
  const [useMailgun, setUseMailgun] = useState(false);
  const [mailgunResults, setMailgunResults] = useState<Map<string, MailgunBulkValidationResult>>(new Map());
  const [validatingWithMailgun, setValidatingWithMailgun] = useState(false);
  const [mailgunProgress, setMailgunProgress] = useState({completed: 0, total: 0});

  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({completed: 0, total: 0});
  const [importSucceeded, setImportSucceeded] = useState(0);
  const [importFailures, setImportFailures] = useState<{email: string; error: string}[]>([]);

  // Identifies the current import run. Closing the modal bumps it, which cancels
  // an in-flight loop instead of letting it keep creating users and then stomp
  // on the reset state when it finishes.
  const importRunIdRef = useRef(0);
  // Whether the current run created anyone, so a cancelled run still refreshes
  // the parent's user list for the users it did create.
  const createdAnyRef = useRef(false);

  const [createUser] = useMutation<{createUser: boolean}>(CREATE_USER);

  // The target organisation's own roles (built-in and custom), filtered to what we may hand out.
  const {data: rolesData} = useQuery(ORGANISATION_ROLES_QUERY, {
    variables: {id: organisationId},
    skip: !isOpen || !organisationId,
    fetchPolicy: 'cache-first',
  });
  const roles = useMemo(() => grantableRoles(rolesData?.organisation?.roles), [rolesData]);
  const roleName = (roleId: string | undefined) => roles.find((role) => role.id === roleId)?.name ?? roleId;

  // Auto-detect separator when text changes
  useEffect(() => {
    if (autoDetect && emailText.trim()) {
      const detected = autoDetectSeparator(emailText);
      setDetectedSeparator(detected);
      setCustomSeparators([detected]);
    }
  }, [emailText, autoDetect]);

  // Parse emails
  const parsedEmails = useMemo(() => {
    if (!emailText.trim()) return [];
    return parseEmailList(emailText, customSeparators);
  }, [emailText, customSeparators]);

  // Get statistics
  const stats = useMemo(() => {
    return getEmailListStats(parsedEmails);
  }, [parsedEmails]);

  // Find issues
  const dangerousWarnings = useMemo(() => {
    return findDangerousDomains(parsedEmails);
  }, [parsedEmails]);

  const duplicates = useMemo(() => {
    return findDuplicateEmails(parsedEmails);
  }, [parsedEmails]);

  const hasWarnings = dangerousWarnings.length > 0;
  const hasErrors = stats.invalid > 0 || duplicates.size > 0;
  const canProceed = stats.valid > 0 && (!hasErrors || ignoreWarnings);

  const resetModal = () => {
    setStep('input');
    setEmailText('');
    setAutoDetect(true);
    setDetectedSeparator('\n');
    setCustomSeparators(['\n']);
    setShowAdvanced(false);
    setIgnoreWarnings(false);
    setEditableEmails([]);
    setEditingIndex(null);
    setEditEmail('');
    setEditFirstName('');
    setEditLastName('');
    setEditDescription('');
    setEditRoleId('');
    setMailgunApiKey('');
    setUseMailgun(false);
    setMailgunResults(new Map());
    setValidatingWithMailgun(false);
    setMailgunProgress({completed: 0, total: 0});
    setImporting(false);
    setImportProgress({completed: 0, total: 0});
    setImportSucceeded(0);
    setImportFailures([]);
  };

  const handleClose = () => {
    // Cancel any in-flight import before resetting, so its loop stops issuing
    // mutations and stops writing to state.
    importRunIdRef.current++;
    const createdBeforeClose = createdAnyRef.current;
    createdAnyRef.current = false;
    resetModal();
    onClose();
    // Users created before the cancellation are real; let the parent refetch.
    if (createdBeforeClose) {
      onSuccess();
    }
  };

  const handleNext = () => {
    if (step === 'input') {
      // Initialize editable emails with only valid, unique emails
      const validEmails = parsedEmails.filter((e) => e.valid);
      const uniqueMap = new Map<string, ParsedEmail>();
      validEmails.forEach((email) => {
        const key = email.email.toLowerCase();
        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, email);
        }
      });
      setEditableEmails(Array.from(uniqueMap.values()));
      setStep('review');
    }
  };

  const handleBack = () => {
    if (step === 'review') {
      setStep('input');
    }
  };

  const handleImport = async () => {
    const runId = ++importRunIdRef.current;
    createdAnyRef.current = false;

    setImporting(true);
    setImportSucceeded(0);
    setImportFailures([]);
    setImportProgress({completed: 0, total: editableEmails.length});

    const failures: {email: string; error: string}[] = [];
    let succeeded = 0;

    // One user at a time: each import sends an invitation email, and a per-user
    // result lets us report exactly which addresses failed.
    for (const entry of editableEmails) {
      // Closing the modal bumps the run id; stop before creating anyone else.
      if (importRunIdRef.current !== runId) return;

      const username = entry.email.toLowerCase();
      try {
        const {data} = await createUser({
          variables: {
            input: {
              organisationId,
              username,
            },
          },
        });
        if (data?.createUser) {
          succeeded++;
          createdAnyRef.current = true;
        } else {
          failures.push({email: username, error: 'Server rejected the user without an error message'});
        }
      } catch (err: any) {
        failures.push({email: username, error: err?.message ?? 'Unknown error'});
      }

      // The await above yields, so re-check before touching state.
      if (importRunIdRef.current !== runId) return;
      setImportProgress((prev) => ({...prev, completed: prev.completed + 1}));
    }

    setImportSucceeded(succeeded);
    setImportFailures(failures);
    setImporting(false);
    setStep('result');
  };

  const handleDeleteEmail = (index: number) => {
    setEditableEmails((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStartEdit = (index: number) => {
    const email = editableEmails[index];
    setEditingIndex(index);
    setEditEmail(email.email);
    setEditFirstName(email.firstName || '');
    setEditLastName(email.lastName || '');
    setEditDescription(email.description || '');
    setEditRoleId(email.roleId || '');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditEmail('');
    setEditFirstName('');
    setEditLastName('');
    setEditDescription('');
    setEditRoleId('');
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null && editEmail.trim()) {
      setEditableEmails((prev) => {
        const updated = [...prev];
        const firstName = editFirstName.trim() || undefined;
        const lastName = editLastName.trim() || undefined;
        const fullName = [firstName, lastName].filter(Boolean).join(' ') || undefined;

        updated[editingIndex] = {
          email: editEmail.trim(),
          name: fullName,
          firstName,
          lastName,
          description: editDescription.trim() || undefined,
          roleId: editRoleId.trim() || undefined,
          valid: true,
        };
        return updated;
      });
      // Clear Mailgun result for edited email
      if (mailgunResults.has(editEmail.trim().toLowerCase())) {
        setMailgunResults((prev) => {
          const updated = new Map(prev);
          updated.delete(editEmail.trim().toLowerCase());
          return updated;
        });
      }
      handleCancelEdit();
    }
  };

  const handleValidateWithMailgun = async () => {
    if (!mailgunApiKey.trim()) {
      alert('Please enter a Mailgun API key');
      return;
    }

    setValidatingWithMailgun(true);
    setMailgunProgress({completed: 0, total: editableEmails.length});

    try {
      const emailsToValidate = editableEmails.map((e) => e.email);
      const results = await validateEmailsBulkWithMailgun(emailsToValidate, mailgunApiKey, (completed, total) => {
        setMailgunProgress({completed, total});
      });

      // Store results in a map for quick lookup
      const resultMap = new Map<string, MailgunBulkValidationResult>();
      results.forEach((result) => {
        resultMap.set(result.email.toLowerCase(), result);
      });
      setMailgunResults(resultMap);

      // Filter out emails that Mailgun says are undeliverable
      const validEmails = editableEmails.filter((email) => {
        const result = resultMap.get(email.email.toLowerCase());
        return !result || result.valid;
      });
      setEditableEmails(validEmails);

      setValidatingWithMailgun(false);
    } catch (error: any) {
      console.error('Mailgun validation error:', error);
      alert(`Mailgun validation failed: ${error.message}`);
      setValidatingWithMailgun(false);
    }
  };

  const handleFinish = () => {
    createdAnyRef.current = false;
    onSuccess();
    handleClose();
  };

  const getSeparatorDisplayName = (sep: string): string => {
    switch (sep) {
      case '\n':
        return 'Newline';
      case ',':
        return 'Comma (,)';
      case ';':
        return 'Semicolon (;)';
      case ' ':
        return 'Space';
      default:
        return sep;
    }
  };

  const toggleSeparator = (sep: string) => {
    setAutoDetect(false);
    setCustomSeparators((prev) => {
      if (prev.includes(sep)) {
        const updated = prev.filter((s) => s !== sep);
        return updated.length > 0 ? updated : ['\n'];
      } else {
        return [...prev, sep];
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <Users size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Bulk User Import</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Import users to {organisationName}</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <X size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Progress indicator */}
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className={`flex items-center space-x-2 ${step === 'input' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                    step === 'input' ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  1
                </div>
                <span className="text-sm font-medium">Input Emails</span>
              </div>
              <div className="flex-1 h-0.5 mx-4 bg-gray-200 dark:bg-gray-700" />
              <div className={`flex items-center space-x-2 ${step === 'review' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                    step === 'review' ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  2
                </div>
                <span className="text-sm font-medium">Review & Confirm</span>
              </div>
              <div className="flex-1 h-0.5 mx-4 bg-gray-200 dark:bg-gray-700" />
              <div className={`flex items-center space-x-2 ${step === 'result' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`}>
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                    step === 'result' ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  3
                </div>
                <span className="text-sm font-medium">Complete</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Step 1: Input */}
            {step === 'input' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Paste Email Addresses</label>
                  <textarea
                    value={emailText}
                    onChange={(e) => setEmailText(e.target.value)}
                    rows={12}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={`Paste email addresses here. Supported formats:\n\njohn@example.com\nJohn Doe <john@example.com>\njane@example.com, bob@example.com\nalice@example.com; charlie@example.com`}
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                    Supports plain emails (john@example.com) and RFC 5322 format (John Doe &lt;john@example.com&gt;)
                  </p>
                </div>

                {/* Separator settings */}
                <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 space-y-3">
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center justify-between w-full text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    <div className="flex items-center space-x-2">
                      <Settings size={16} />
                      <span>Separator Settings</span>
                    </div>
                    {showAdvanced ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>

                  {showAdvanced && (
                    <div className="space-y-3 pt-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="autoDetect"
                          checked={autoDetect}
                          onChange={(e) => setAutoDetect(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="autoDetect" className="text-sm text-gray-700 dark:text-gray-300">
                          Auto-detect separator
                        </label>
                      </div>

                      {autoDetect && detectedSeparator && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 ml-6">Detected: {getSeparatorDisplayName(detectedSeparator)}</div>
                      )}

                      {!autoDetect && (
                        <div className="ml-6 space-y-2">
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Select one or more separators:</p>
                          {['\n', ',', ';', ' '].map((sep) => (
                            <div key={sep} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                id={`sep-${sep}`}
                                checked={customSeparators.includes(sep)}
                                onChange={() => toggleSeparator(sep)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <label htmlFor={`sep-${sep}`} className="text-sm text-gray-700 dark:text-gray-300">
                                {getSeparatorDisplayName(sep)}
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Live preview stats */}
                {emailText.trim() && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <FileText size={16} className="text-blue-600 dark:text-blue-400" />
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100">Preview</h4>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-blue-600 dark:text-blue-400">Total</p>
                        <p className="text-lg font-bold text-blue-900 dark:text-blue-100">{stats.total}</p>
                      </div>
                      <div>
                        <p className="text-green-600 dark:text-green-400">Valid</p>
                        <p className="text-lg font-bold text-green-900 dark:text-green-100">{stats.valid}</p>
                      </div>
                      <div>
                        <p className="text-red-600 dark:text-red-400">Invalid</p>
                        <p className="text-lg font-bold text-red-900 dark:text-red-100">{stats.invalid}</p>
                      </div>
                      <div>
                        <p className="text-purple-600 dark:text-purple-400">Domains</p>
                        <p className="text-lg font-bold text-purple-900 dark:text-purple-100">{stats.uniqueDomains}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Review */}
            {step === 'review' && (
              <div className="space-y-6">
                {/* Mailgun Validation Section */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                      <Shield size={20} className="text-purple-600 dark:text-purple-400" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-purple-900 dark:text-purple-100">Mailgun Email Validation</h4>
                          <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                            Verify deliverability of email addresses using Mailgun's API
                          </p>
                        </div>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={useMailgun}
                            onChange={(e) => setUseMailgun(e.target.checked)}
                            className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-purple-900 dark:text-purple-100">Enable</span>
                        </label>
                      </div>

                      {useMailgun && (
                        <div className="space-y-3">
                          <div className="flex items-center space-x-3">
                            <input
                              type="password"
                              value={mailgunApiKey}
                              onChange={(e) => setMailgunApiKey(e.target.value)}
                              placeholder="Enter Mailgun API Key"
                              className="flex-1 px-3 py-2 text-sm border border-purple-300 dark:border-purple-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-purple-400 dark:placeholder-purple-600"
                            />
                            <button
                              onClick={handleValidateWithMailgun}
                              disabled={!mailgunApiKey.trim() || validatingWithMailgun || editableEmails.length === 0}
                              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                            >
                              {validatingWithMailgun ? (
                                <>
                                  <RefreshCw size={16} className="animate-spin" />
                                  <span>Validating...</span>
                                </>
                              ) : (
                                <>
                                  <Shield size={16} />
                                  <span>Validate</span>
                                </>
                              )}
                            </button>
                          </div>

                          {validatingWithMailgun && (
                            <div className="flex items-center space-x-2 text-sm text-purple-700 dark:text-purple-300">
                              <div className="flex-1 bg-purple-200 dark:bg-purple-800 rounded-full h-2 overflow-hidden">
                                <div
                                  className="bg-purple-600 dark:bg-purple-400 h-full transition-all duration-300"
                                  style={{width: `${(mailgunProgress.completed / mailgunProgress.total) * 100}%`}}
                                />
                              </div>
                              <span>
                                {mailgunProgress.completed} / {mailgunProgress.total}
                              </span>
                            </div>
                          )}

                          {mailgunResults.size > 0 && !validatingWithMailgun && (
                            <div className="text-sm text-purple-700 dark:text-purple-300">
                              ✓ Validated {mailgunResults.size} email{mailgunResults.size !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Summary stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                    <p className="text-sm text-green-600 dark:text-green-400">Total Users</p>
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">{editableEmails.length}</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <p className="text-sm text-blue-600 dark:text-blue-400">Unique Domains</p>
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                      {new Set(editableEmails.map((e) => e.email.split('@')[1]?.toLowerCase())).size}
                    </p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                    <p className="text-sm text-purple-600 dark:text-purple-400">With Names</p>
                    <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{editableEmails.filter((e) => e.name).length}</p>
                  </div>
                </div>

                {/* Warnings */}
                {hasWarnings && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle size={20} className="text-orange-600 dark:text-orange-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-orange-900 dark:text-orange-100 mb-2">Free Email Providers Detected</h4>
                        <p className="text-sm text-orange-800 dark:text-orange-300 mb-3">
                          The following emails use free email providers (Gmail, Yahoo, etc.). For enterprise organizations, consider using corporate
                          email addresses.
                        </p>
                        <div className="space-y-2">
                          {dangerousWarnings.map((warning) => (
                            <div key={warning.domain} className="text-sm">
                              <span className="font-medium text-orange-900 dark:text-orange-100">{warning.domain}</span>
                              <span className="text-orange-700 dark:text-orange-400 ml-2">
                                ({warning.count} {warning.count === 1 ? 'email' : 'emails'})
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Errors */}
                {stats.invalid > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertCircle size={20} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">Invalid Email Addresses</h4>
                        <p className="text-sm text-red-800 dark:text-red-300 mb-3">
                          {stats.invalid} email address{stats.invalid !== 1 ? 'es' : ''} could not be validated. These will be skipped during import.
                        </p>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {parsedEmails
                            .filter((e) => !e.valid)
                            .slice(0, 10)
                            .map((email, idx) => (
                              <div key={idx} className="text-sm font-mono text-red-700 dark:text-red-400">
                                {email.email} - {email.error}
                              </div>
                            ))}
                          {parsedEmails.filter((e) => !e.valid).length > 10 && (
                            <div className="text-sm text-red-600 dark:text-red-400">
                              ...and {parsedEmails.filter((e) => !e.valid).length - 10} more
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Duplicates */}
                {duplicates.size > 0 && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertCircle size={20} className="text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-yellow-900 dark:text-yellow-100 mb-2">Duplicate Emails Detected</h4>
                        <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-3">
                          {duplicates.size} email address{duplicates.size !== 1 ? 'es appear' : ' appears'} multiple times. Only one instance of each
                          will be imported.
                        </p>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {Array.from(duplicates.entries())
                            .slice(0, 10)
                            .map(([email, count]) => (
                              <div key={email} className="text-sm font-mono text-yellow-700 dark:text-yellow-400">
                                {email} (appears {count} times)
                              </div>
                            ))}
                          {duplicates.size > 10 && (
                            <div className="text-sm text-yellow-600 dark:text-yellow-400">...and {duplicates.size - 10} more</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Ignore warnings checkbox */}
                {hasErrors && (
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="ignoreWarnings"
                      checked={ignoreWarnings}
                      onChange={(e) => setIgnoreWarnings(e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="ignoreWarnings" className="text-sm text-gray-700 dark:text-gray-300">
                      I understand the issues and want to proceed anyway
                    </label>
                  </div>
                )}

                {/* Editable user list */}
                {editableEmails.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">Users to Import ({editableEmails.length})</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Click edit to modify or delete to remove</p>
                    </div>
                    <div className="max-h-96 overflow-y-auto space-y-2">
                      {editableEmails.map((email, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700"
                        >
                          {editingIndex === idx ? (
                            // Edit mode
                            <div className="flex-1 flex flex-col space-y-3 py-2">
                              <div className="flex items-start space-x-3">
                                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                                  <Users size={14} className="text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                                  {/* Email (readonly, used as username) */}
                                  <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                      Email (Username) <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="email"
                                      value={editEmail}
                                      onChange={(e) => setEditEmail(e.target.value)}
                                      placeholder="email@domain.com"
                                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
                                    />
                                  </div>

                                  {/* First Name */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">First Name</label>
                                    <input
                                      type="text"
                                      value={editFirstName}
                                      onChange={(e) => setEditFirstName(e.target.value)}
                                      placeholder="John"
                                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    />
                                  </div>

                                  {/* Last Name */}
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Last Name</label>
                                    <input
                                      type="text"
                                      value={editLastName}
                                      onChange={(e) => setEditLastName(e.target.value)}
                                      placeholder="Doe"
                                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    />
                                  </div>

                                  {/* Description */}
                                  <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description / Job Title</label>
                                    <input
                                      type="text"
                                      value={editDescription}
                                      onChange={(e) => setEditDescription(e.target.value)}
                                      placeholder="Marketing Manager"
                                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    />
                                  </div>

                                  {/* Role */}
                                  <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Role</label>
                                    <select
                                      value={editRoleId}
                                      onChange={(e) => setEditRoleId(e.target.value)}
                                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                    >
                                      <option value="">-- Select Role --</option>
                                      {roles.map((role) => (
                                        <option key={role.id} value={role.id}>
                                          {role.name}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                </div>
                                <div className="flex flex-col space-y-1">
                                  <button
                                    onClick={handleSaveEdit}
                                    disabled={!editEmail.trim()}
                                    className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors disabled:opacity-50"
                                    title="Save changes"
                                  >
                                    <Check size={18} />
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                    title="Cancel"
                                  >
                                    <X size={18} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            // View mode
                            <>
                              <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                                  <Users size={14} className="text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  {email.name && <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{email.name}</p>}
                                  <div className="flex items-center space-x-2">
                                    <p className="text-gray-600 dark:text-gray-400 font-mono text-xs truncate">{email.email}</p>
                                    {mailgunResults.has(email.email.toLowerCase()) && (
                                      <span
                                        className={`px-2 py-0.5 text-xs rounded ${
                                          mailgunResults.get(email.email.toLowerCase())?.valid
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                                        }`}
                                        title={getMailgunResultDescription(mailgunResults.get(email.email.toLowerCase())?.result)}
                                      >
                                        {mailgunResults.get(email.email.toLowerCase())?.result?.result || 'Validated'}
                                      </span>
                                    )}
                                  </div>
                                  {(email.description || email.roleId) && (
                                    <div className="flex items-center space-x-2 mt-1">
                                      {email.description && <span className="text-xs text-gray-500 dark:text-gray-400">{email.description}</span>}
                                      {email.roleId && (
                                        <span className="px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                                          {roleName(email.roleId)}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => handleStartEdit(idx)}
                                  className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                  title="Edit email"
                                >
                                  <Edit2 size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteEmail(idx)}
                                  className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                  title="Remove email"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Result */}
            {step === 'result' && (
              <div className="space-y-6">
                <div className="text-center py-8">
                  <div
                    className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                      importSucceeded > 0 ? 'bg-green-100 dark:bg-green-900/20' : 'bg-red-100 dark:bg-red-900/20'
                    }`}
                  >
                    {importSucceeded > 0 ? (
                      <CheckCircle size={32} className="text-green-600 dark:text-green-400" />
                    ) : (
                      <AlertCircle size={32} className="text-red-600 dark:text-red-400" />
                    )}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                    {importFailures.length === 0 ? 'Import Successful!' : importSucceeded > 0 ? 'Import Partially Completed' : 'Import Failed'}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Imported {importSucceeded} of {importProgress.total} user{importProgress.total !== 1 ? 's' : ''} to {organisationName}
                  </p>
                  {importSucceeded > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 max-w-md mx-auto">
                      <p className="text-sm text-blue-800 dark:text-blue-300">Users will receive invitation emails to set up their accounts.</p>
                    </div>
                  )}
                </div>

                {importFailures.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertCircle size={20} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-red-900 dark:text-red-100 mb-2">
                          {importFailures.length} user{importFailures.length !== 1 ? 's' : ''} could not be imported
                        </h4>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {importFailures.map((failure, idx) => (
                            <div key={`${failure.email}-${idx}`} className="text-sm">
                              <span className="font-mono text-red-700 dark:text-red-400">{failure.email}</span>
                              <span className="text-red-600 dark:text-red-400"> - {failure.error}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between p-6 border-t border-gray-200 dark:border-gray-700">
            {step === 'input' && (
              <>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleNext}
                  disabled={!emailText.trim() || stats.valid === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next: Review
                </button>
              </>
            )}

            {step === 'review' && (
              <>
                <button
                  onClick={handleBack}
                  disabled={importing}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
                <button
                  onClick={handleImport}
                  disabled={editableEmails.length === 0 || importing || editingIndex !== null}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {importing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>
                        Importing {importProgress.completed} / {importProgress.total}...
                      </span>
                    </>
                  ) : (
                    <>
                      <Upload size={16} />
                      <span>Import Users</span>
                    </>
                  )}
                </button>
              </>
            )}

            {step === 'result' && (
              <>
                <div />
                <button onClick={handleFinish} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  Finish
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
